// Page changes: one sequence, owned here.
//
//   exit   the page on screen dissolves (plain module fades) into the next
//          page's background; the shell plays its part at the same time:
//            home -> work   logo and local time stay, their colour follows the
//                           background as it comes up
//            work -> work   header and work chrome stay, colours fade at 520ms
//            work -> home   header, work chrome and menu button fade out
//   swap   once the screen is covered: old sections hidden, scroll to top,
//          the router shows the next page (the old one is gone in the same commit)
//   land   a work page's section controller takes the covering layer over and
//          builds its first section; the home lifts it and plays its intro
import { exitPage } from "./exit"
import { mixBars } from "./bars"
import type { Tone } from "../shell/context"
import { Arrival, Handoff, Sections } from "../work/sections"

export type PageInfo = { kind: "home" | "work" | "about"; tone: Tone; stops: number[][] }

export type TransitionHost = {
    current: () => string
    info: (routeId: string) => PageInfo
    swap: (routeId: string, hash?: string) => void
    shell: {
        headerMix: (to: Tone, d: number) => void
        headerTone: (tone: Tone, fade: boolean) => void
        chromeTone: (tone: Tone) => void
        leaveForHome: () => void
        leaveWork: () => void
    }
}

let leavingAt = 0
/** a page change is under way */
export const changing = () => !!leavingAt && Date.now() - leavingAt < 2500

/** The section mostly on screen, or the home stage. */
function onScreen(): HTMLElement | null {
    return Sections.onScreen() || (document.querySelector("main.nto27") as HTMLElement | null)
}

function scrollTop() {
    // the top of the page opened (on a work page the categories before it sit above)
    const o = document.querySelector<HTMLElement>("[data-nto-origin]")
    const top = o ? Math.max(0, o.getBoundingClientRect().top + scrollY) : 0
    try {
        window.scrollTo({ top, left: 0, behavior: "instant" as ScrollBehavior })
    } catch {
        window.scrollTo(0, top)
    }
}

/** Mark a fresh landing at the top for the next page's controller. */
function requestTop() {
    document.documentElement.style.scrollSnapType = "none"
    Arrival.requested = Date.now()
}

export function navigate(host: TransitionHost, to: string, hash?: string) {
    const from = host.current()
    if (to === from) return
    if (leavingAt && Date.now() - leavingAt < 2500) return // one page change at a time
    const a = host.info(from)
    const b = host.info(to)
    const target = onScreen()
    const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches

    if (reduced || !target) {
        if (a.kind === "work" && b.kind === "work") host.shell.chromeTone(b.tone)
        if (b.kind !== "home") host.shell.headerTone(b.tone, false)
        requestTop()
        Sections.leave?.()
        scrollTop()
        host.swap(to, hash)
        return
    }

    leavingAt = Date.now()
    // (for what sits outside the page, over it: going too, e.g. the home's award tab)
    window.dispatchEvent(new Event("nto:leave"))
    // the work page's controller stops gliding and snapping right away
    Sections.leave?.()

    let swapped = false
    const swap = () => {
        if (swapped) return
        swapped = true
        // fully covered: hide the old sections so none can flash during the jump
        Sections.get().forEach((el) => (el.style.visibility = "hidden"))
        requestTop()
        scrollTop()
        // the header's colour has followed the background; settle it
        if (toWork) host.shell.headerTone(b.tone, false)
        host.swap(to, hash)
        window.setTimeout(() => (leavingAt = 0), 900)
    }

    // (the header follows the new page's colours on the way to a work page or the about)
    const toWork = b.kind !== "home"
    const others = Sections.get().filter((el) => el !== target && el.isConnected)
    // the home carousel: one sweep on desktop, all panels at once on a phone
    const track = (document.querySelector("[data-card-index]") as HTMLElement | null)?.parentElement || null
    const phone = matchMedia("(max-width: 809px), (pointer: coarse)").matches

    const h = exitPage(target, {
        duration: 1300,
        stops: b.stops,
        also: others,
        sweep: track && !phone ? track : null,
        whole: track && phone ? track : null,
        onCovered: swap,
        // the header's colour follows the background coming up (only until the
        // swap: then it settles on the new page's colours)
        // Safari's bars change along with it too (src/app/bars.ts), handed the
        // colour a moment ahead since they show it late
        onDark: (d: number, ahead: number) => {
            if (swapped) return
            if (toWork) host.shell.headerMix(b.tone, d)
            mixBars(b.stops, ahead)
        },
    })
    Handoff.layer = h
    Handoff.at = Date.now()

    if (a.kind === "work" && b.kind === "work") {
        // (early enough to be done before the swap: the next page's first render holds
        // every animation for a moment, which froze this fade and made it jump)
        window.setTimeout(() => host.shell.chromeTone(b.tone), 250)
    } else if (b.kind === "home") host.shell.leaveForHome()
    else if (a.kind === "work") host.shell.leaveWork()

    h.finished.then(() => window.setTimeout(swap, 60))
}

/** After the swap: a work page's controller has taken the covering layer;
 *  the home lifts it and plays its own intro. */
export function arrived(kind: "home" | "work" | "about") {
    if (!Handoff.layer) return
    scrollTop()
    if (kind !== "work") Handoff.lift(Handoff.take(), 600, "cubic-bezier(.5,0,.5,1)")
}
